/**
 * One business's tasks for one day, reached by clicking its name in Robots.
 *
 * The day and filters arrive in the query string, so this page can be linked to or
 * refreshed, and Back returns to the day's table exactly as it was left — same day,
 * same status, same business selection.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import BusinessTaskTable from '../components/robots/BusinessTaskTable';
import { SessionExpiredError } from '../api/session';
import {
  dailyTasksPath,
  dayLabel,
  fetchDailyTasks,
  pacificToday,
  parseStatusFilter,
  statusLabel,
  summarizeByBusiness,
  type DailyTasksReport,
} from '../api/robotDailyTasks';
import './Page.css';

const BusinessDailyTasks: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const id = Number(businessId);
  const date = searchParams.get('date') || pacificToday();
  const status = parseStatusFilter(searchParams.get('status'));
  /** The table's own business filter, carried through so Back restores it. */
  const businessFilter = searchParams.get('business') ?? '';

  const [report, setReport] = useState<DailyTasksReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    fetchDailyTasks(date, status, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setReport(next);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) return;
        setReport(null);
        setError(
          err instanceof SessionExpiredError
            ? 'Your session expired. Sign in again to load these tasks.'
            : err instanceof Error
              ? err.message
              : 'Failed to load the tasks for this business.'
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [date, status]);

  // The day is fetched whole and narrowed here, which keeps this page's counts
  // identical to the row that was clicked to reach it.
  const business = useMemo(
    () =>
      report && Number.isInteger(id)
        ? summarizeByBusiness(report.rows).find((b) => b.businessId === id) ?? null
        : null,
    [report, id]
  );

  const goBack = () => navigate(dailyTasksPath({ date, status, business: businessFilter }));

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">

          <div>
            <PageHeader
              title={business?.businessName ?? 'Daily Tasks'}
              subtitle={dayLabel(date)}
              onBack={goBack}
              backLabel="Back to Daily Tasks"
            />
          </div>

          <div className="page-content">
            {error ? (
              <p className="create-order-error" role="alert">{error}</p>
            ) : loading ? (
              <p className="page-subtitle">Loading tasks…</p>
            ) : business ? (
              <BusinessTaskTable business={business} status={status} />
            ) : (
              // Either the business ran nothing that day or nothing matching the
              // filter, which from here look the same — both are answered by going
              // back and picking a different day or status.
              <p className="page-subtitle">
                No {status ? `${statusLabel(status).toLowerCase()} ` : ''}tasks were recorded at this
                business on {dayLabel(date)}.
              </p>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default BusinessDailyTasks;
